import React, { useCallback, useMemo, useState } from 'react';

import { Typography } from '@mui/material';
import clsx from 'clsx';
import { AlineTooltip, PICKER_CHIP_ICON_SIZE, usePickerChipStyles } from 'src/components/generic';
import { ConfirmationDialog } from 'src/components/generic/ConfirmationDialog';
import { ProgressiveColumnIcon } from 'src/components/workflow/board/settings/ColumnItem';
import {
  getColumnPositionInCategory,
  getIsPausedOrCancelledCategory,
  getPhaseHomeColumnOptions,
} from 'src/components/workflow/phase-column-helpers';
import { ColumnPickerPopover } from 'src/components/workflow/WorkflowBoardRelocationDialog';
import type { Apollo } from 'src/gql/generated';
import { useBoardColumns } from 'src/hooks/boards';
import { useUpdatePhase } from 'src/hooks/phase';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  chip: {
    alignItems: 'center',
    cursor: 'pointer',
    display: 'flex',
    flexShrink: 0,
    gap: theme.spacing(0.5),
    maxWidth: 180,
  },
  chipDisabled: {
    cursor: 'default',
    opacity: 0.6,
  },
  chipLabel: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  lockedChip: {
    cursor: 'default',
  },
}));

interface PhaseHomeColumnChipProps {
  boardId: string;
  isPhaseActive: boolean;
  isPhaseCompleted: boolean;
  isSigningPhase: boolean;
  phase: Pick<Apollo.PhaseSimpleFragment, 'column' | 'id'>;
  workflowColumnCategory: Apollo.ColumnCategory;
}

export const PhaseHomeColumnChip = React.memo(function PhaseHomeColumnChip({
  boardId,
  isPhaseActive,
  isPhaseCompleted,
  isSigningPhase,
  phase,
  workflowColumnCategory,
}: PhaseHomeColumnChipProps) {
  const { classes } = useStyles();
  const { updatePhase } = useUpdatePhase();
  const [unparkConfirmOpen, setUnparkConfirmOpen] = useState(false);
  const [pendingColumnId, setPendingColumnId] = useState<UUID | null>(null);
  const { getPickerChipClassName, pickerChipIconClassName } = usePickerChipStyles();
  const { boardColumns, refetchBoardColumns, signingColumnId } = useBoardColumns(boardId);

  const columnOptions = useMemo(
    () =>
      getPhaseHomeColumnOptions({
        columns: boardColumns,
        currentColumn: phase.column,
        isSigningPhase,
        signingColumnId,
      }),
    [boardColumns, isSigningPhase, phase.column, signingColumnId]
  );
  const columnPosition = useMemo(() => {
    if (!columnOptions.length) return { index: 1, total: 1 };
    return getColumnPositionInCategory(phase.column, columnOptions);
  }, [columnOptions, phase.column]);
  const pendingColumn = columnOptions.find(column => column.id === pendingColumnId);

  const applyColumnChange = useCallback(
    async (columnId: string, confirmUnpark = false) => {
      if (columnId === phase.column.id) return;
      await updatePhase({
        variables: {
          input: {
            confirmUnpark,
            phaseId: phase.id,
            props: { columnId },
          },
        },
      });
    },
    [phase.column.id, phase.id, updatePhase]
  );

  const handleColumnChange = useCallback(
    (columnId: string) => {
      if (columnId === phase.column.id || isPhaseCompleted) return;

      const targetColumn = columnOptions.find(column => column.id === columnId);
      if (!targetColumn) return;

      const needsUnparkConfirm =
        isPhaseActive &&
        getIsPausedOrCancelledCategory(workflowColumnCategory) &&
        !getIsPausedOrCancelledCategory(targetColumn.category);

      if (needsUnparkConfirm) {
        setPendingColumnId(columnId);
        setUnparkConfirmOpen(true);
        return;
      }

      void applyColumnChange(columnId);
    },
    [
      applyColumnChange,
      columnOptions,
      isPhaseActive,
      isPhaseCompleted,
      phase.column.id,
      workflowColumnCategory,
    ]
  );

  const handleConfirmUnpark = useCallback(() => {
    if (pendingColumnId) void applyColumnChange(pendingColumnId, true);
    setPendingColumnId(null);
    setUnparkConfirmOpen(false);
  }, [applyColumnChange, pendingColumnId]);

  const tooltipTitle = `On phase start, move to "${phase.column.name}"`;
  const chipContent = (
    <>
      <ProgressiveColumnIcon
        boxSize={PICKER_CHIP_ICON_SIZE}
        category={phase.column.category}
        className={pickerChipIconClassName}
        color={phase.column.color}
        iconSize={12}
        index={columnPosition.index}
        showBackground={false}
        showBorder={false}
        total={columnPosition.total}
      />
      <Typography className={classes.chipLabel} variant="body2">
        {phase.column.name}
      </Typography>
    </>
  );

  if (isSigningPhase) {
    return (
      <AlineTooltip arrow placement="top" title={tooltipTitle}>
        <div className={clsx(classes.chip, classes.lockedChip)}>{chipContent}</div>
      </AlineTooltip>
    );
  }

  if (isPhaseCompleted) {
    return (
      <AlineTooltip arrow placement="top" title={tooltipTitle}>
        <div className={clsx(classes.chip, classes.chipDisabled)}>{chipContent}</div>
      </AlineTooltip>
    );
  }

  return (
    <>
      <ColumnPickerPopover
        addStatusOption={{ boardId, label: 'Add Started status' }}
        columns={columnOptions}
        emptyLabel="No statuses"
        renderTrigger={(_, isOpen) => (
          <AlineTooltip arrow placement="top" title={tooltipTitle}>
            <div className={getPickerChipClassName(isOpen, classes.chip)}>{chipContent}</div>
          </AlineTooltip>
        )}
        selectedColumnId={phase.column.id}
        onColumnChange={handleColumnChange}
        onOpen={refetchBoardColumns}
      />
      <ConfirmationDialog
        message={
          pendingColumn
            ? `Move to "${pendingColumn.name}" and resume work on this phase?`
            : 'Move to the selected column and resume work on this phase?'
        }
        open={unparkConfirmOpen}
        primaryText="Move and Resume"
        title="Resume from pause?"
        onClickPrimary={handleConfirmUnpark}
        onClose={() => {
          setPendingColumnId(null);
          setUnparkConfirmOpen(false);
        }}
      />
    </>
  );
});
